import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { FileRejection } from 'react-dropzone';
import { Tooltip } from '@heroui/react';
import { Reorder } from 'motion/react';
import type { ContentStep } from '@/entities/Courses';
import { Svg } from '@/shared/ui/Svg';
import { MessageModal } from '@/shared/ui/Modal/MessageModal';
import { useToast } from '@/shared/lib/hooks/useToast';
import PlusIcon from '@/shared/assets/icons/plus.svg';
import { StepCard, tooltipClassNames, type StepMediaKind } from './StepCard';
import {
  SESSION_STEP_LIMIT,
  createSessionStep,
  type SessionStepDraft,
  type StepLiveActions,
} from './types';
import styles from './SessionStepsField.module.scss';

interface SessionStepsFieldProps {
  steps: SessionStepDraft[];
  onChange: Dispatch<SetStateAction<SessionStepDraft[]>>;
  maxSteps?: number;
  minSteps?: number;
  // Edit mode: deleting a saved step also removes students' progress
  warnAboutProgress?: boolean;
  // Latest server state of the steps, used to show video and SCORM processing status
  serverSteps?: ContentStep[];
  onPreviewScorm?: (packageId: number) => void;
  isScormPreviewLoading?: boolean;
  // Runs before a step is added; returning false cancels it (e.g. the session form is invalid)
  onBeforeAdd?: () => boolean | Promise<boolean>;
  // Edit mode: every change is saved immediately through these actions
  live?: StepLiveActions;
}

const rejectionReason = ({ file, errors }: FileRejection) => {
  const codes = errors.map((error) => error.code);
  if (codes.includes('file-too-large')) return `${file.name} is too large`;
  if (codes.includes('file-invalid-type')) return `${file.name} has an unsupported file type`;
  return errors[0]?.message ? `${file.name}: ${errors[0].message}` : file.name;
};

export const SessionStepsField = ({
  steps,
  onChange,
  maxSteps = SESSION_STEP_LIMIT,
  minSteps = 1,
  warnAboutProgress = false,
  serverSteps = [],
  onPreviewScorm,
  isScormPreviewLoading = false,
  onBeforeAdd,
  live,
}: SessionStepsFieldProps) => {
  const { toast } = useToast();
  const dragStartStepsRef = useRef<SessionStepDraft[] | null>(null);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);
  const [mediaToRemove, setMediaToRemove] = useState<{
    step: SessionStepDraft;
    stepNumber: number;
    kind: StepMediaKind;
  } | null>(null);
  const [scormToReplace, setScormToReplace] = useState<{
    step: SessionStepDraft;
    stepNumber: number;
    file: File;
  } | null>(null);

  const canAdd = steps.length < maxSteps && !live?.isAdding;
  const canDelete = steps.length > minSteps;

  const handleRejected = (rejections: FileRejection[]) => {
    toast({
      title: 'File not added',
      description: rejections.map(rejectionReason).join('. '),
      color: 'danger',
    });
  };

  const updateStep = (clientId: string, patch: Partial<SessionStepDraft>) => {
    onChange((current) =>
      current.map((step) => (step.clientId === clientId ? { ...step, ...patch } : step)),
    );
  };

  const addStep = async () => {
    if (!canAdd) return;
    if (live) {
      await live.addStep();
      return;
    }
    if (onBeforeAdd && !(await onBeforeAdd())) return;
    onChange((current) => [
      ...current.map((step) => ({ ...step, expanded: false })),
      createSessionStep(),
    ]);
  };

  const confirmDelete = () => {
    if (stepToDelete === null) return;
    const step = steps[stepToDelete];
    setStepToDelete(null);
    if (!step) return;
    if (live) live.deleteStep(step);
    else onChange((current) => current.filter((item) => item.clientId !== step.clientId));
  };

  const confirmRemoveMedia = () => {
    if (!mediaToRemove) return;
    const { step, kind } = mediaToRemove;
    setMediaToRemove(null);
    if (live) {
      if (kind === 'video') live.removeVideo(step);
      else live.removeScorm(step);
    } else {
      updateStep(step.clientId, kind === 'video' ? { removeVideo: true } : { removeScorm: true });
    }
  };

  const confirmReplaceScorm = () => {
    if (!scormToReplace) return;
    const { step, stepNumber, file } = scormToReplace;
    setScormToReplace(null);
    live?.startScormUpload(step, stepNumber, file);
  };

  // Cards swap places live while dragging; the order is saved once the card is dropped
  const reorderByIds = (ids: string[]) => {
    onChange((current) =>
      ids
        .map((id) => current.find((step) => step.clientId === id))
        .filter((step): step is SessionStepDraft => !!step),
    );
  };

  const finishDrag = () => {
    const previous = dragStartStepsRef.current;
    dragStartStepsRef.current = null;
    if (!live || !previous) return;
    const changed = steps.some((step, index) => step.clientId !== previous[index]?.clientId);
    if (changed) live.reorder(steps, previous);
  };

  const moveStep = (from: number, to: number) => {
    if (from === to || to < 0 || to >= steps.length) return;
    const next = [...steps];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
    if (live) live.reorder(next, steps);
  };

  const deleteStepNumber = stepToDelete !== null ? stepToDelete + 1 : 0;
  const deleteWithProgress =
    warnAboutProgress && stepToDelete !== null && !!steps[stepToDelete]?.serverId;
  const whenSaved = live ? '' : ' after you save changes';

  return (
    <div className={styles.list}>
      {live && steps.length > 0 && (
        <p className="text-xs text-grey-secondary">Changes to steps are saved automatically.</p>
      )}
      <Reorder.Group
        as="div"
        axis="y"
        values={steps.map((step) => step.clientId)}
        onReorder={reorderByIds}
        className={styles.list}
      >
        {steps.map((step, index) => (
          <StepCard
            key={step.clientId}
            step={step}
            index={index}
            serverStep={serverSteps.find((item) => item.id === step.serverId)}
            canDelete={canDelete}
            live={live}
            onUpdate={(patch) => updateStep(step.clientId, patch)}
            onRequestDelete={() => setStepToDelete(index)}
            onRequestRemoveMedia={(kind) =>
            setMediaToRemove({ step, stepNumber: index + 1, kind })
          }
            onRequestReplaceScorm={(file) =>
            setScormToReplace({ step, stepNumber: index + 1, file })
          }
            onRejected={handleRejected}
            onPreviewScorm={onPreviewScorm}
            isScormPreviewLoading={isScormPreviewLoading}
            onDragStart={() => {
            dragStartStepsRef.current = steps;
          }}
            onDragEnd={finishDrag}
            onMove={(direction) => moveStep(index, index + direction)}
        />
        ))}
      </Reorder.Group>
      <Tooltip
        content={`You can add up to ${maxSteps} ${maxSteps === 1 ? 'step' : 'steps'} to a session`}
        placement="top-start"
        isDisabled={steps.length < maxSteps}
        classNames={tooltipClassNames}
      >
        <span className="w-fit">
          <button type="button" className={styles.addStep} onClick={addStep} disabled={!canAdd}>
            <Svg Icon={PlusIcon} width={20} height={20} />
            {live?.isAdding ? 'Adding step...' : 'Add step'}
          </button>
        </span>
      </Tooltip>
      <MessageModal
        isOpen={stepToDelete !== null}
        onOpenChange={() => setStepToDelete(null)}
        title={`Delete Step ${deleteStepNumber}?`}
        text={
          deleteWithProgress
            ? `Step ${deleteStepNumber} and students' progress will be permanently deleted. This action cannot be undone`
            : `Step ${deleteStepNumber} will be permanently deleted. This action cannot be undone`
        }
        confirmText="Delete step"
        variant={deleteWithProgress ? 'red' : 'black'}
        toggleVisibility={confirmDelete}
      />
      <MessageModal
        isOpen={mediaToRemove !== null}
        onOpenChange={() => setMediaToRemove(null)}
        title={mediaToRemove?.kind === 'scorm' ? 'Delete SCORM package?' : 'Delete video?'}
        text={
          mediaToRemove?.kind === 'scorm'
            ? `The SCORM package of Step ${mediaToRemove.stepNumber} and students' SCORM progress will be permanently deleted${whenSaved}. This action cannot be undone`
            : `The video of Step ${mediaToRemove?.stepNumber ?? ''} will be permanently deleted${whenSaved}. This action cannot be undone`
        }
        confirmText={mediaToRemove?.kind === 'scorm' ? 'Delete package' : 'Delete video'}
        variant={mediaToRemove?.kind === 'scorm' ? 'red' : 'black'}
        toggleVisibility={confirmRemoveMedia}
      />
      <MessageModal
        isOpen={scormToReplace !== null}
        onOpenChange={() => setScormToReplace(null)}
        title="Replace SCORM package?"
        text={`The current SCORM package of Step ${scormToReplace?.stepNumber ?? ''} and students' SCORM progress will be permanently deleted. This action cannot be undone`}
        confirmText="Replace package"
        variant="red"
        toggleVisibility={confirmReplaceScorm}
      />
    </div>
  );
};
